import { toast } from '@/hooks/useToast';
import {
  getGetDepartmentByIDQueryKey,
  getGetTravellerByIDQueryKey,
  TravellerWithCollectionMembershipsDTO,
  useDeleteCollectionMemberByID,
} from '@/api/trafikportalen';
import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTenantID } from '@/hooks/useTenantID';
import { useIntl } from 'react-intl';
import {
  isPartialDeleteTravellerProductsError,
  useDeleteTravellerAssociatedProducts,
} from '@/hooks/useDeleteTravellerAssociatedProducts';

export const useCollectionMemberDelete = () => {
  const tenantId = useTenantID();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const deleteCollectionMember = useDeleteCollectionMemberByID();
  const { deleteAssociatedProducts } = useDeleteTravellerAssociatedProducts();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(
    async (
      traveller: TravellerWithCollectionMembershipsDTO,
      collectionMemberID: string,
    ) => {
      setIsDeleting(true);
      let associatedProductsDeleted = false;
      try {
        if (traveller.memberships.length === 1) {
          await deleteAssociatedProducts(traveller);
          associatedProductsDeleted = true;
        }

        await deleteCollectionMember.mutateAsync({
          collectionMemberID,
        });

        toast.success(
          {
            title: formatMessage({
              defaultMessage: `Resenären har tagits bort från avdelningen`,
            }),
          },
          { autoClose: 3000 },
        );

        const travellerQueryKey = getGetTravellerByIDQueryKey(
          traveller.traveller.id,
        );
        const departmentInvalidation = queryClient.invalidateQueries({
          queryKey: getGetDepartmentByIDQueryKey(
            traveller.memberships.find(
              (membership) =>
                membership.collection_member_id === collectionMemberID,
            )?.department_id ?? '',
          ),
        });

        if (traveller.memberships.length === 1) {
          // The traveller has left the organisation and can no longer be
          // fetched, so leave the page instead of refetching it.
          await navigate({
            to: '/$tenantId/departments',
            params: { tenantId },
            search: {
              departmentId: traveller.memberships[0].department_id,
            },
            replace: false,
          });
          queryClient.removeQueries({ queryKey: travellerQueryKey });
        } else {
          await queryClient.invalidateQueries({ queryKey: travellerQueryKey });
        }
        await departmentInvalidation;
      } catch (error) {
        console.error('Error', error);
        const isPartialFailure =
          isPartialDeleteTravellerProductsError(error) ||
          associatedProductsDeleted;
        toast.error(
          {
            title: isPartialFailure
              ? formatMessage({
                  defaultMessage:
                    'Kunde inte slutföra borttagningen. Vissa biljetter, kort eller Allpay-konton kan redan ha tagits bort. Resenären har inte tagits bort från avdelningen.',
                })
              : formatMessage({ defaultMessage: 'Ett fel inträffade' }),
          },
          { autoClose: 3000 },
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [
      deleteAssociatedProducts,
      deleteCollectionMember,
      formatMessage,
      navigate,
      queryClient,
      tenantId,
    ],
  );

  return {
    deleteCollectionMember: handleDelete,
    isLoading: isDeleting,
  };
};
